import {
	DataGridPremium,
	type DataGridPremiumProps,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	type GridValidRowModel,
	useGridApiRef,
	useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium'
import { LicenseInfo } from '@mui/x-license'
import { useMemo } from 'react'

export const CHECKBOX_COL_DEF = {
	...GRID_CHECKBOX_SELECTION_COL_DEF,
	width: 65,
} as const

// Keep this one close to the DataGridPremium usage.
LicenseInfo.setLicenseKey(
	// License key for @mui/x-data-grid-premium renewed on 2026-04-23
	'1026262f9a975d529a956f80b06d7d00Tz0xMjI1NTMsRT0xNzk2MzQyMzk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9UTEtMjAyNixRPTEsS1Y9Mg=='
)

// When the height of a row is set to "auto", the final height will follow exactly the content
// size and ignore the density. Add padding to the cells to increase the space between the
// content and the cell borders.
const AUTO_ROW_HEIGHT = () => 'auto' as const
const AUTO_ROW_HEIGHT_SX = {
	'.MuiDataGrid-cell': {
		py: 2,
	},
}

const CLICKABLE_ROWS_DATA_GRID_SX = {
	'.MuiDataGrid-cell:focus': {
		outline: 'none',
	},
	'& .MuiDataGrid-cell:hover': {
		cursor: 'pointer',
	},
}

const NON_CLICKABLE_ROWS_DATA_GRID_SX = {
	'&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
		{
			outline: 'none',
		},
}
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const
type Props<T extends GridValidRowModel> = Omit<
	DataGridPremiumProps<T>,
	'getRowHeight' | 'apiRef'
> &
	(
		| {
				hasAutoHeightRows?: true
				getRowHeight?: never
				sx?: never
				clickableCell?: boolean
		  }
		| {
				hasAutoHeightRows?: false
				getRowHeight?: DataGridPremiumProps<T>['getRowHeight']
				sx?: DataGridPremiumProps<T>['sx']
				clickableCell?: boolean
		  }
	)

const DataGrid = <T extends GridValidRowModel>({
	getRowHeight,
	hasAutoHeightRows,
	sx,
	clickableCell,
	pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
	disableMultipleColumnsSorting = true,
	initialState,
	...props
}: Props<T>) => {
	const apiRef = useGridApiRef()
	const datagridInitialState = useKeepGroupedColumnsHidden({
		apiRef,
		initialState,
	})
	const memoizedSx = useMemo(
		() => ({
			...(hasAutoHeightRows ? AUTO_ROW_HEIGHT_SX : sx),
			...(clickableCell
				? CLICKABLE_ROWS_DATA_GRID_SX
				: NON_CLICKABLE_ROWS_DATA_GRID_SX),
		}),
		[clickableCell, hasAutoHeightRows, sx]
	)

	return (
		<DataGridPremium<T>
			{...props}
			getRowHeight={hasAutoHeightRows ? AUTO_ROW_HEIGHT : getRowHeight}
			sx={memoizedSx}
			pageSizeOptions={pageSizeOptions}
			disableMultipleColumnsSorting={disableMultipleColumnsSorting}
			initialState={datagridInitialState}
			apiRef={apiRef}
		/>
	)
}

export { DataGrid }
